import React, { useState } from 'react';
import { apiService } from '../services/api';
import './OnboardingForm.css';

interface OnboardingData {
  phone: string;
  timezone: string;
  messagingEmail?: string;
  dailySummaryEnabled: boolean;
}

interface OnboardingFormProps {
  onComplete: (updatedUserData?: any, formData?: OnboardingData) => void;
  initialData?: OnboardingData;
  userName?: string;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({ onComplete, initialData, userName }) => {
  const [formData, setFormData] = useState<OnboardingData>(initialData || {
    phone: '',
    timezone: 'America/New_York',
    messagingEmail: '',
    dailySummaryEnabled: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Helper function to get current time in a timezone
  const getTimeInTimezone = (timezone: string): string => {
    try {
      const now = new Date();
      return now.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if required fields are filled
    const requiredFields: (keyof OnboardingData)[] = ['phone', 'timezone'];
    const missingFields = requiredFields.filter(field => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields`);
      setLoading(false);
      return;
    }

    try {
      const result = await apiService.completeOnboarding(formData);
      onComplete(result.user, formData);
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-layout">
        {/* Left side - Form */}
        <div className="onboarding-form-section">
          <div className="form-content">
            <div className="form-header">
              <h1 className="form-title">Hey {userName || 'there'}, almost done!</h1>
            </div>

            <form onSubmit={handleSubmit} className="onboarding-form">
              <div className="form-group phone-group">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  required
                />
              </div>

              <div className="form-group">
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                  className="timezone-select"
                >
                  <option value="">Select Timezone</option>
                  <option value="America/New_York">Eastern Time (ET) - {getTimeInTimezone('America/New_York')}</option>
                  <option value="America/Chicago">Central Time (CT) - {getTimeInTimezone('America/Chicago')}</option>
                  <option value="America/Denver">Mountain Time (MT) - {getTimeInTimezone('America/Denver')}</option>
                  <option value="America/Phoenix">Arizona Time (MST) - {getTimeInTimezone('America/Phoenix')}</option>
                  <option value="America/Los_Angeles">Pacific Time (PT) - {getTimeInTimezone('America/Los_Angeles')}</option>
                  <option value="America/Anchorage">Alaska Time (AKT) - {getTimeInTimezone('America/Anchorage')}</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HST) - {getTimeInTimezone('Pacific/Honolulu')}</option>
                  <option value="Europe/London">London (GMT/BST) - {getTimeInTimezone('Europe/London')}</option>
                  <option value="Europe/Paris">Paris (CET/CEST) - {getTimeInTimezone('Europe/Paris')}</option>
                  <option value="Europe/Berlin">Berlin (CET/CEST) - {getTimeInTimezone('Europe/Berlin')}</option>
                  <option value="Asia/Dubai">Dubai (GST) - {getTimeInTimezone('Asia/Dubai')}</option>
                  <option value="Asia/Kolkata">India (IST) - {getTimeInTimezone('Asia/Kolkata')}</option>
                  <option value="Asia/Singapore">Singapore (SGT) - {getTimeInTimezone('Asia/Singapore')}</option>
                  <option value="Asia/Tokyo">Tokyo (JST) - {getTimeInTimezone('Asia/Tokyo')}</option>
                  <option value="Australia/Sydney">Sydney (AEDT/AEST) - {getTimeInTimezone('Australia/Sydney')}</option>
                </select>
              </div>

              <div className="form-group">
                <input
                  type="email"
                  id="messagingEmail"
                  name="messagingEmail"
                  value={formData.messagingEmail || ''}
                  onChange={handleInputChange}
                  placeholder="Messaging Email (optional, e.g., your iCloud email)"
                />
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="dailySummaryEnabled"
                  name="dailySummaryEnabled"
                  checked={formData.dailySummaryEnabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="dailySummaryEnabled">
                  Receive daily updates via text message
                </label>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="submit-button-container">
                <button
                  type="submit"
                  className="nav-button submit-button"
                  disabled={loading}
                  title="Complete Setup"
                >
                  {loading ? 'Submitting...' : 'Complete Setup'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right side - Garden background */}
        <div
          className="onboarding-welcome"
          style={{
            backgroundImage: `url('/garden-image.jpg')`
          }}
        >
        </div>
      </div>
    </div>
  );
};

export default OnboardingForm;
